import {
  type CatalogueItemResource,
  CatalogueItemsController_list_v1,
  type OutletResource,
  OutletsController_list_v1,
  type StockMovementResource,
  StockMovementsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isNotFound } from '@/api/failure';
import { describeRecordingGap, formatInstant } from '@/lib/instant';

import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { isRetired, labelForKind } from '../outlets/outlet-kinds';
import { salePath } from '../paths';
import {
  causeOf,
  formatMovementQuantity,
  iconForReason,
  labelForReason,
  type MovementCause,
} from './movement-reasons';

/**
 * Why the figure is what it is: `GET /v1/outlets/{outletId}/stock/movements`.
 *
 * The levels screen beside this one reports a number. This one is the account
 * behind it — every movement that summed to it, newest first, with what caused
 * each. A level of −2 is a real state the API never clamps (CL-501), and the
 * only way anybody settles one is by reading the movements that produced it.
 *
 * `movement-reasons.ts` owns the three readings that lose information in a cell
 * — the sign, the reason and the cause — and argues each at length; nothing here
 * restates them.
 *
 * ## The list is paged, and this is the case paging exists for
 *
 * `docs/ui-conventions.md`, "Reading a list", asks for the strategy to be picked
 * deliberately. This is case 2 and it is the clearest instance of it in the app:
 * the movement log grows with **use** — a row per line of every sale, every
 * refund, every delivery and every counted variance, for ever — so it is the one
 * set in this epic that is unbounded by the park. `collectPages` would be a loop
 * that gets slower every month and never says so.
 *
 * Contrast the levels screen, which reads whole and has no pager at all: a
 * position is one consistent snapshot and a page of it would be half a snapshot.
 * Two screens over the same subject, two strategies, both deliberate.
 *
 * `Pager` and the sums in `pagination.ts` unchanged — the 200 answers `{ items,
 * total, limit, offset }`, which is `PageState` exactly.
 *
 * ## Ordered by the server's clock, and the column says so
 *
 * Every movement carries both clocks. `occurredAt` is whatever caused it — a
 * till's clock where a device was involved — and **it orders nothing** (FR-008,
 * FR-506). `recordedAt` is when the server accepted it and is authoritative for
 * ordering (FR-009); this log is newest first by that field, and a level is the
 * sum of the movements bounded by it (R-508).
 *
 * So the Recorded column leads with `recordedAt`, which is **the opposite of
 * what the sales list does** and the difference is not an inconsistency. A sale
 * answers "when did this customer pay", which is the till's clock; a movement
 * log answers "why does this figure read what it reads", and the figure is
 * computed from the server's. Leading with the other clock would print a column
 * that appears to be out of order on a screen whose whole value is that its
 * order is trustworthy.
 *
 * The other clock is named underneath when the two are far enough apart to be
 * saying something — a till that sold through a three-hour outage appears here
 * where it synced, and the second line is the account of that outage. The
 * threshold is `lib/instant.ts`'s and the reasoning is there: a second line
 * under every row is a line everybody stops reading.
 *
 * ## Reach is the server's
 *
 * `@RequireRole()` with no arguments plus `@RequireOutletScope()`, so the route
 * is `requireSignedIn()` on this side and what a caller may see is decided in
 * the `WHERE` clause. An outlet outside that reach answers 404 exactly as an
 * unknown one does (FR-128, and the contract is explicit that an empty page
 * would be wrong here because the route is keyed on `{outletId}`) — so the
 * screen says "there is no such outlet" and does not guess which of the three it
 * was.
 *
 * ## What is not here
 *
 * **No reason filter.** The route takes `?catalogueItemId=` and the page
 * parameters, and nothing else. Filtering by reason in the browser would filter
 * the fifty rows on this page rather than the set, which is a control that
 * quietly answers a different question from the one it appears to.
 *
 * **Nothing writes.** A delivery, a correction and a stocktake are the three
 * acts that move stock from the back office and each is its own ticket with its
 * own `@RequireRole('OWNER', 'MANAGER')` guard to declare; a till moves stock as
 * a side effect of selling and refunding, which is why this epic ships no till
 * screen at all.
 */

/** Every state the rows can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly movements: readonly StockMovementResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The value the item picker carries when it is not filtering. */
const ANY = 'ALL';

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 5;

export function StockMovements() {
  const client = useApiClient();

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [outletId, setOutletId] = useState<string | null>(null);

  // Whether the outlets have answered *at all*, which is not the same question
  // as whether there are any — the same distinction the levels screen draws, and
  // for the same reason: without it the notice under an empty picker reads "no
  // outlet is in reach from this account" for as long as the first request
  // takes, which is a sentence about somebody's assignments said before anything
  // is known about them.
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  // What the outlet sells, which is this screen's only source of an item's code
  // and name: a movement names a `catalogueItemId` and carries neither.
  const [items, setItems] = useState<readonly CatalogueItemResource[]>([]);
  const [itemsAnswered, setItemsAnswered] = useState(false);

  const [catalogueItemId, setCatalogueItemId] = useState<string>(ANY);

  const [offset, setOffset] = useState(0);

  const [state, setState] = useState<ListState>({ kind: 'idle' });

  // The window the last successful read answered with, kept **outside**
  // `ListState` on purpose: it survives the next load, so the pager and the
  // count stay where they are while a page is in flight instead of vanishing and
  // coming back. `null` until the first answer.
  const [page, setPage] = useState<PageState | null>(null);

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  // Any filter moving puts the list back at the first page. Left alone, a filter
  // applied at offset 200 lands on a page past the end of a shorter result and
  // reads as "nothing matched" — `pageAfter` clamps a *step*, and this is not
  // one.
  const changeFilter = useCallback((apply: () => void) => {
    apply();
    setOffset(0);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. `GET /v1/outlets` is
    // scoped by the same rule this route is (FR-111), so the picker offers what
    // the reader may actually read and the ordinary path never asks for an
    // outlet that would answer 404.
    collectPages<OutletResource>((from, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);

        // One outlet in reach is the common case for anybody who is not an owner
        // or a manager, and asking them to pick from a list of one is a control
        // that cannot do anything. Never overrides a choice already made.
        setOutletId((chosen) => chosen ?? (all.length === 1 ? (all[0]?.id ?? null) : null));
      })
      .catch(() => {
        // The picker is the only casualty and the screen says so where the
        // picker is, rather than replacing the whole page with an error about a
        // control. Not `outletsAnswered`: a failed read has answered nothing
        // about who is assigned where.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  useEffect(() => {
    if (outletId === null) {
      setItems([]);
      setItemsAnswered(false);

      return;
    }

    const controller = new AbortController();
    let live = true;

    setItemsAnswered(false);

    // Read whole, and this is the read-whole case rather than a size judgement
    // slipped past: the catalogue fills a picker that needs every option at
    // once, and it is also the only thing that turns each row's
    // `catalogueItemId` into a code and a name. A page of it would leave the
    // fifty-first item nameless in the table.
    //
    // `includeRetired` because a movement log is history: an item retired last
    // month still sold last month, and excluding it would blank the name on
    // every row that names it while leaving the rows themselves in place.
    //
    // `outletId` because the picker's job is the movements of *this* outlet, and
    // an item the outlet never sold answers an empty page — a control whose
    // options mostly produce nothing is not a filter.
    collectPages<CatalogueItemResource>((from, limit) =>
      client.request(CatalogueItemsController_list_v1, {
        query: { offset: from, limit, outletId, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setItems(all);
        setItemsAnswered(true);
      })
      .catch(() => {
        // The names and the filter are the casualty; the log itself still reads.
        // `itemsAnswered` stays false, which is what keeps the Item column from
        // claiming an item is no longer sold here when the truth is that nothing
        // was ever asked. See `ItemCell`.
        if (live) setItems([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, reloads]);

  useEffect(() => {
    if (outletId === null) {
      setState({ kind: 'idle' });

      return;
    }

    // Two guards, not one. `controller` stops the request itself the moment a
    // filter moves, and `live` stops an answer that was already in flight from
    // landing after this effect was torn down — without it, switching outlets
    // twice quickly can leave the second outlet's rows underneath the first's.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(StockMovementsController_list_v1, {
        path: { outletId },
        query: {
          offset,
          limit: ROWS_PER_PAGE,

          // Sent only when it is filtering. The query string is what appears in
          // a log when somebody is working out what a screen asked for, and a
          // `catalogueItemId=ALL` in one is a filter that does not exist — which
          // the API would refuse outright rather than ignore.
          ...(catalogueItemId === ANY ? {} : { catalogueItemId }),
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (!live) return;

        setState({ kind: 'loaded', movements: answer.items });

        // The window the *server* answered with, not the one that was asked for.
        // They are the same today, and a pager that assumed so would be silently
        // wrong the day the API clamps a limit.
        setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, catalogueItemId, offset, reloads]);

  // Base UI resolves what a trigger shows from `items` and not from the rendered
  // options, so a picker without it shows a bare UUID where a name belongs.
  // Derived from the same rows the options render, so the two cannot drift.
  const outletItems = useMemo(
    () => outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [outlets],
  );

  // Only what tracks stock. An item that does not track it can never have a
  // movement (FR-212), so offering one is offering a filter whose answer is
  // guaranteed to be an empty page.
  const stockItems = useMemo(() => items.filter((item) => item.tracksStock), [items]);

  const itemOptions = useMemo(
    () => [
      { label: 'Every item', value: ANY },
      ...stockItems.map((item) => ({ label: `${item.code} — ${item.name}`, value: item.id })),
    ],
    [stockItems],
  );

  // Every item the catalogue answered with, not only the stock-tracked ones: the
  // filter should not offer an item that cannot move, but a row that names one
  // still deserves its name rather than a fallback.
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const outlet = outlets.find((candidate) => candidate.id === outletId);

  const missing = state.kind === 'failed' && isNotFound(state.error);

  const movements: readonly StockMovementResource[] =
    state.kind === 'loaded' ? state.movements : [];

  // Whether this page holds a cause that is named but cannot be opened, which is
  // what decides whether the note under the table is drawn. Computed from the
  // rows rather than shown always: on a page of nothing but sales and deliveries
  // it would be a paragraph explaining an absence nobody can see.
  const unreachableCause = movements.some((movement) => {
    const cause = causeOf(movement);

    return cause.kind === 'refund' || cause.kind === 'stocktake';
  });

  return (
    <Screen
      title="Why the figure moved"
      description="Every movement behind an outlet's levels, newest first by when the server accepted it. Each one names what caused it — a sale, a refund, a count — so a level can be explained rather than only reported."
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="outlet-picker" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                changeFilter(() => {
                  setOutletId(next);

                  // An item id names an item at one outlet and nothing at
                  // another, and the two filters together would answer an empty
                  // page that reads as "nothing has ever moved here". Cleared
                  // rather than carried across.
                  setCatalogueItemId(ANY);
                });
              }}
            >
              <SelectTrigger
                id="outlet-picker"
                aria-label="Outlet"
                // Nothing to choose between until the outlets land, and a picker
                // with no options is a control that cannot do anything.
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue placeholder="Choose an outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outlets.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Rendered once there is an outlet to have a catalogue. Disabled
              rather than hidden while it is loading, so the control does not
              appear and shift the rail under a pointer. */}
          {outletId !== null && (
            <div className="space-y-2">
              <Label htmlFor="item-filter" className="text-sm text-muted-foreground">
                Item
              </Label>
              <Select
                value={catalogueItemId}
                items={itemOptions}
                // `null` is Base UI's "cleared", which this picker has no
                // affordance for. Read as "not filtering" rather than cast away,
                // so a cleared select can never become a `catalogueItemId=null`
                // in the query string.
                onValueChange={(next) => {
                  changeFilter(() => setCatalogueItemId(next ?? ANY));
                }}
              >
                <SelectTrigger
                  id="item-filter"
                  aria-label="Item"
                  disabled={stockItems.length === 0}
                  className="h-10 w-full bg-card"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={ANY}>Every item</SelectItem>
                    {stockItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.code} — {item.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          {outlet !== undefined && (
            <ScreenFact label="Outlet">
              <span className="font-mono numeric">{outlet.code}</span> — {outlet.name}
              {isRetired(outlet) && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an outlet is an
                      ordinary end to an ordinary life. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
              <br />
              <span className="text-muted-foreground">{labelForKind(outlet.kind)}</span>
            </ScreenFact>
          )}

          {page !== null && state.kind !== 'failed' && !missing && (
            <ScreenFact label="Matching">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'movement' : 'movements'}
            </ScreenFact>
          )}
        </>
      }
    >
      {missing ? (
        <OutletMissing />
      ) : (
        <>
          {/* `stated`, with a width on **every** column, added by FE068 when the
              sheet took the whole field and the `68rem` cap went.

              Item was the one column naming no width, so it took every pixel of
              the slack: at 2000px it was about 900px holding "Cardamom sachet,
              50g", and the trip from an item to the reason it moved crossed a
              void. Rendering settled two things that reasoning would not have.
              **`columns="stated"` on its own changes nothing here** — one
              unstated column is handed the leftover under either layout, so
              both put the hole in the same place. What closes it is stating a
              width on all five: fixed layout then shares the leftover out in
              proportion, every column grows a little and none of them opens a
              gap. `docs/design-language.md`, under **Open**, cut "state them
              all" against Outlets — where one column genuinely wanted the room.
              Here none does, which is what makes the same move right. */}
          <ScreenTable label="Stock movements" columns="stated">
            <TableHeader>
              <TableRow>
                {/* Named for the clock it leads with, which is the one the list
                    is ordered by and the one a level is computed against. */}
                <TableHead className="w-52">Recorded</TableHead>
                <TableHead className="w-64">Item</TableHead>
                <TableHead className="w-52">Why</TableHead>
                <TableHead className="w-44">Who</TableHead>
                {/* Quantities right-align, per docs/ui-conventions.md —
                    comparing down a column is the task. */}
                <TableHead className="w-28 text-right">Moved</TableHead>
              </TableRow>
            </TableHeader>

            {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

            {state.kind === 'idle' && (
              <TableNotice columns={COLUMNS}>
                <EmptyState
                  title="Choose an outlet"
                  description={
                    outletsAnswered && outlets.length === 0
                      ? 'No outlet is in reach from this account. Somebody who staffs the park assigns the outlets a person can read.'
                      : 'Stock moves at an outlet, so there is no park-wide log to show. Pick one and its movements appear here.'
                  }
                />
              </TableNotice>
            )}

            {state.kind === 'failed' && (
              <TableNotice columns={COLUMNS}>
                <LoadFailed error={state.error} onRetry={reload} />
              </TableNotice>
            )}

            {state.kind === 'loaded' &&
              (movements.length === 0 ? (
                <TableNotice columns={COLUMNS}>
                  <EmptyState
                    title={
                      catalogueItemId === ANY ? 'Nothing has moved yet' : 'This item has not moved'
                    }
                    description={emptyDescription(catalogueItemId !== ANY)}
                  />
                </TableNotice>
              ) : (
                <TableBody>
                  {/* In the order the API sent them, which is newest first by
                      `recordedAt`. Nothing here sorts, filters or adds a row. */}
                  {movements.map((movement) => (
                    <MovementRow
                      key={movement.id}
                      movement={movement}
                      item={itemsById.get(movement.catalogueItemId)}
                      itemsAnswered={itemsAnswered}
                    />
                  ))}
                </TableBody>
              ))}
          </ScreenTable>

          {/* Not while the load failed: the notice above already says the list
              could not be read, and a pager under it offers to move through rows
              that are not there. `Pager` draws nothing when everything fits on
              one page. */}
          {page !== null && state.kind !== 'failed' && (
            <Pager state={page} label="stock movements" onOffsetChange={setOffset} />
          )}

          {unreachableCause && <UnreachableCauses />}
        </>
      )}
    </Screen>
  );
}

/**
 * What an empty page should say, which depends on why it is empty.
 *
 * The filtered case is worth its own sentence because the API is explicit about
 * it: an item this outlet has never moved answers an **empty page**, not a
 * refusal — the 404 on this route is about the outlet in the path and never
 * about the filter. So "nothing matched" here means exactly what it says and
 * there is nothing wrong.
 *
 * There is no "record the first one" on either branch. Every act that moves
 * stock belongs to another screen — a sale is rung up at a till, and a delivery,
 * a correction and a stocktake are their own back-office routes — so a call to
 * action here would point at a control this screen does not have.
 */
function emptyDescription(filtering: boolean): string {
  if (filtering) {
    return 'This outlet has never moved this item. Nothing is wrong — an item it stocks but has neither sold nor taken in simply has no history yet. Choose another item, or every item, to see the rest of the log.';
  }

  return 'No stock has moved at this outlet. Movements are written as things happen: a till selling or refunding writes one for each item, and a delivery, a correction or a stocktake writes one from the back office.';
}

/**
 * One movement: when the server took it, what moved, why, who, and how many.
 *
 * The row carries no controls. This screen reads — the only thing to click is
 * the cause, which goes to the record that produced the movement.
 */
function MovementRow({
  movement,
  item,
  itemsAnswered,
}: {
  readonly movement: StockMovementResource;

  /** The catalogue row this movement names, where the catalogue had one. */
  readonly item: CatalogueItemResource | undefined;

  /** Whether the catalogue has answered at all — see `ItemCell`. */
  readonly itemsAnswered: boolean;
}) {
  const Icon = iconForReason(movement.reason);

  // `null` unless the two clocks are far enough apart to be saying something.
  // Used as the test rather than rendered, because what belongs under a recorded
  // instant is the other instant and not a description of the distance.
  const gap = describeRecordingGap(movement.occurredAt, movement.recordedAt);

  return (
    <TableRow>
      <TableCell className="whitespace-normal">
        <span className="text-foreground numeric">{formatInstant(movement.recordedAt)}</span>

        {gap !== null && (
          <span className="mt-0.5 block text-xs text-muted-foreground numeric">
            Happened {formatInstant(movement.occurredAt)}
          </span>
        )}
      </TableCell>

      <TableCell className="whitespace-normal">
        <ItemCell
          item={item}
          catalogueItemId={movement.catalogueItemId}
          itemsAnswered={itemsAnswered}
        />
      </TableCell>

      <TableCell className="whitespace-normal">
        {/* `outline` in every case. Eight reasons cannot be eight colours in a
            palette that has two, and none of them is a refusal — see
            `movement-reasons.ts`. The icon is what tells them apart. */}
        <Badge variant="outline">
          {/* `data-icon` is the registry's own hook for the tighter padding a
              badge with a leading icon wants. Sized by the badge, not here. */}
          <Icon data-icon="inline-start" aria-hidden />
          {labelForReason(movement.reason)}
        </Badge>

        <Cause cause={causeOf(movement)} />
      </TableCell>

      <TableCell className="whitespace-normal">
        <span className="text-foreground">{movement.actor.displayName}</span>

        {/* A staff code is read aloud and typed back in, so it gets the mono face
            and tabular figures. Absent where the actor is not a person — a
            SYSTEM actor has no users row and so no code — and an empty line is
            the honest rendering of that rather than a placeholder claiming
            something. */}
        {movement.actor.staffCode !== null && (
          <span className="mt-0.5 block font-mono text-xs text-muted-foreground numeric">
            {movement.actor.staffCode}
          </span>
        )}
      </TableCell>

      <TableCell className="text-right">
        <span className="font-mono text-foreground numeric">
          {formatMovementQuantity(movement.quantity)}
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * Which item moved — its code and its name, or the most honest thing available
 * when the catalogue could not supply them.
 *
 * A movement names a `catalogueItemId` and carries no code and no name, so this
 * column is a join the client makes and the join can miss. The two ways it
 * misses say different things and are deliberately not collapsed:
 *
 * - **The catalogue has not answered.** It is still in flight, or its request
 *   failed and left the filter empty. Nothing is known about this item, so the
 *   cell says nothing — an em dash, which claims nothing the server did not.
 * - **The catalogue answered and this item was not in it.** The outlet moved the
 *   item at some point and no longer sells it: availability was withdrawn, and a
 *   withdrawn item leaves the catalogue while its movements stay in the log for
 *   ever. That is a real and readable fact, so it is said — and the id is
 *   printed with it, because it is then the only identifier of the row that
 *   exists and somebody reconciling a shelf needs *something* to quote.
 *
 * Printing a raw UUID is otherwise against the house rule and this is the
 * sanctioned exception: the rule exists because a UUID is noise where a name is
 * derivable, and here there is nothing to derive it from.
 */
function ItemCell({
  item,
  catalogueItemId,
  itemsAnswered,
}: {
  readonly item: CatalogueItemResource | undefined;
  readonly catalogueItemId: string;
  readonly itemsAnswered: boolean;
}) {
  if (item !== undefined) {
    return (
      <>
        <span className="font-mono text-foreground numeric">{item.code}</span>
        <span className="mt-0.5 block text-sm text-muted-foreground">{item.name}</span>
      </>
    );
  }

  if (!itemsAnswered) return <span className="text-muted-foreground">—</span>;

  return (
    <>
      <span className="text-muted-foreground">No longer sold at this outlet</span>
      <span className="mt-0.5 block font-mono text-xs break-all text-muted-foreground">
        {catalogueItemId}
      </span>
    </>
  );
}

/**
 * What made this movement happen, as something to reach where there is anywhere
 * to reach.
 *
 * **This is the point of the screen.** A log that reads `−3 Sold` without a way
 * to open that sale is much less useful than it looks: the question somebody
 * arrives with is "which sale", and answering it by hand means the receipt
 * number, the day, and a search on another screen.
 *
 * Only the sale can be opened today, and that is an API gap rather than a
 * decision taken here — the shipped document publishes `GET /v1/sales/{id}` and
 * **no route that reads a refund or a stocktake back**. Both are write-only:
 * `POST /v1/sales/{id}/refund` answers the refund it just made and nothing ever
 * answers it again, and the same is true of `POST
 * /v1/outlets/{outletId}/stock/stocktakes`. So those two causes are **named and
 * not linked**, which is the whole of what can be done from here; the note under
 * the table says so once, and inventing a link to a screen that cannot exist
 * would be worse than the plain words.
 *
 * A delivery and the four correction reasons draw nothing at all. They have no
 * cause row by design — the movement *is* the record — so an em dash or a "none"
 * would suggest something is missing when nothing is.
 */
function Cause({ cause }: { readonly cause: MovementCause }) {
  switch (cause.kind) {
    case 'sale':
      return (
        // A dotted underline rather than a column of blue links — the same
        // treatment the sales list gives a receipt number and the catalogue
        // gives an amount that leads to its price history.
        //
        // Named per row, because "The sale" repeated down a list of links is no
        // help to somebody navigating by them. The name says what it opens; the
        // link is built from the id the server sent and never from anything
        // derived.
        <Link
          to={salePath(cause.saleId)}
          aria-label="The sale this came off"
          className="mt-1 block w-fit rounded-sm text-xs text-muted-foreground underline decoration-border decoration-dotted underline-offset-4 outline-none hover:text-foreground hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30"
        >
          The sale
        </Link>
      );

    case 'refund':
      return <span className="mt-1 block text-xs text-muted-foreground">A refund</span>;

    case 'stocktake':
      return <span className="mt-1 block text-xs text-muted-foreground">A stocktake</span>;

    case 'none':
      return null;

    case 'unstated':
      // The branch the database's own CHECK makes unreachable
      // (`stock_movements_cause_matches_reason`, FR-505). If it ever renders,
      // the row arrived claiming a cause it did not name — so the row is shown
      // as it stands and the gap is stated, rather than dropped, guessed at or
      // crashed on.
      return <span className="mt-1 block text-xs text-muted-foreground">No cause recorded</span>;
  }
}

/**
 * Why two of the three causes on this page are words rather than links.
 *
 * Said once, under the table, and only on a page that actually contains one —
 * repeated per row it would be a paragraph of apology down a column, and shown
 * always it would explain an absence nobody can see.
 */
function UnreachableCauses() {
  return (
    <p className="text-sm text-muted-foreground">
      A refund and a stocktake are named above but cannot be opened. The API publishes a route that
      reads a sale back and none that reads either of those, so there is no record to link to yet.
    </p>
  );
}

/**
 * There is no such outlet — or it is one this caller cannot reach.
 *
 * The two are one answer by the API's design (FR-128) and this says the one
 * thing that is true of both. Deliberately **not** worded as a permissions
 * refusal: guessing which it was would tell somebody probing for outlets that a
 * particular id exists, which is exactly the enumeration the shared 404 closes.
 *
 * The contract is explicit that this route answers 404 rather than an empty page
 * across the boundary, precisely because it is keyed on `{outletId}` — an empty
 * `200` is a success, and a success is a leak.
 */
function OutletMissing() {
  return (
    <Alert>
      <IconAlertTriangle />
      <AlertTitle>There is no such outlet</AlertTitle>
      <AlertDescription>
        Nothing in the park answers to it. It may have been retired since this screen was opened, or
        opened from a link that is out of date. Pick an outlet from the list to see what has moved.
      </AlertDescription>
    </Alert>
  );
}
